import React, { useRef } from 'react'
import { withTranslation } from 'react-i18next'
import { inject, observer } from 'mobx-react'
import { useAuth0 } from '@auth0/auth0-react'
import { HugeiconsIcon } from '@hugeicons/react'
import { UserIcon, ChevronDownIcon } from '@hugeicons/core-free-icons'
import UserShapefileList from '../../../stores/UserShapefileList.js'
import UserMarkerfileList from '../../../stores/UserMarkerfileList.js'

import SidebarToggleIcon from '../../common/icons/SidebarToggleIcon.jsx'
import ToolbarMenuItem from './ToolbarMenuItem.jsx'
import AccountMenuItems from './AccountMenuItems.jsx'
import ViewMenuItems from './ViewMenuItems.jsx'
import ManageModal from './ManageModal.jsx'
import Language from '../Language.jsx'
import ProBadge from '../../common/ProBadge.jsx'
import { startProCheckout } from '../../../proCheckout'

const menuItemsMap = {
  USER: 'USER',
  FILE: 'FILE',
  VIEW: 'VIEW',
  TOOLS: 'TOOLS',
  MANAGE: 'MANAGE',
  HELP: 'HELP',
  LANGUAGE: 'LANGUAGE'
}

const ImportButtons = observer(({ uiStore, t, openMarkerFileMenu, openShapeFileMenu }) => {
  const isStarter = uiStore.user && uiStore.user.type === 'starter'
  const handleStarterUpgrade = () => startProCheckout({ uiStore, t })

  return (
    <>
      <button
        type='button'
        onClick={isStarter ? handleStarterUpgrade : openMarkerFileMenu}
        className={isStarter ? 'item-controls__filter-btn item-controls__disabled' : ''}
      >
        {t('TOOLBAR.IMPORT_MARKERS')}
        {isStarter && <ProBadge className='map-settings__pro text-small'>{t('GLOBAL.PRO')}</ProBadge>}
      </button>
      <button
        type='button'
        onClick={isStarter ? handleStarterUpgrade : openShapeFileMenu}
        className={isStarter ? 'item-controls__filter-btn item-controls__disabled' : ''}
      >
        {t('TOOLBAR.IMPORT_KMZ')}
        {isStarter && <ProBadge className='map-settings__pro text-small'>{t('GLOBAL.PRO')}</ProBadge>}
      </button>
    </>
  )
})

const Toolbar = ({ uiStore, dataStore, selectionStore, manageModalStore, t, i18n }) => {
  const { logout } = useAuth0()
  const menuRef = useRef(null)

  const handleClickOutside = (event) => {
    if (menuRef.current && !menuRef.current.contains(event.target)) {
      uiStore.closeToolbarMenu()
    }
  }

  const openShapeFileMenu = async () => {
    const map = uiStore && uiStore.map && uiStore.map.leafletElement
    const center = map && typeof map.getCenter === 'function' ? map.getCenter() : null
    const zoom = map && typeof map.getZoom === 'function' ? map.getZoom() : null
    if (uiStore && typeof uiStore.suppressMapBoundsTemporarily === 'function') {
      uiStore.suppressMapBoundsTemporarily(450)
    }
    const prevZoomToSelected = uiStore && typeof uiStore.zoomToSelected !== 'undefined' ? uiStore.zoomToSelected : null
    if (uiStore && prevZoomToSelected !== null) {
      uiStore.zoomToSelected = false
    }
    uiStore.processing = true
    dataStore.selectedAddGroup = dataStore.targetGroups[0]
    const list = new UserShapefileList()
    list.editMode = true
    list.initialMode = 'upload'
    selectionStore.select(
      list,
      { data: { group: dataStore.targetGroups[0], initialMode: 'upload' } }
    )
    await list.load()
    uiStore.processing = false
    selectionStore.toggleSubTools()
    setTimeout(() => {
      if (map && center && zoom != null) {
        map.setView(center, zoom, { animate: false })
      }
      if (uiStore && prevZoomToSelected !== null) {
        uiStore.zoomToSelected = prevZoomToSelected
      }
    }, 0)
  }

  const openMarkerFileMenu = async () => {
    const map = uiStore && uiStore.map && uiStore.map.leafletElement
    const center = map && typeof map.getCenter === 'function' ? map.getCenter() : null
    const zoom = map && typeof map.getZoom === 'function' ? map.getZoom() : null
    if (uiStore && typeof uiStore.suppressMapBoundsTemporarily === 'function') {
      uiStore.suppressMapBoundsTemporarily(450)
    }
    const prevZoomToSelected = uiStore && typeof uiStore.zoomToSelected !== 'undefined' ? uiStore.zoomToSelected : null
    if (uiStore && prevZoomToSelected !== null) {
      uiStore.zoomToSelected = false
    }
    uiStore.processing = true
    dataStore.selectedAddGroup = dataStore.targetGroups[0]
    const list = new UserMarkerfileList()
    list.editMode = true
    list.initialMode = 'upload'
    selectionStore.select(
      list,
      { data: { group: dataStore.targetGroups[0], initialMode: 'upload' } }
    )
    await list.load()
    uiStore.processing = false
    selectionStore.toggleSubTools()
    setTimeout(() => {
      if (map && center && zoom != null) {
        map.setView(center, zoom, { animate: false })
      }
      if (uiStore && prevZoomToSelected !== null) {
        uiStore.zoomToSelected = prevZoomToSelected
      }
    }, 0)
  }

  React.useEffect(() => {
    document.body.dir = i18n.dir()
    document.addEventListener('click', handleClickOutside)

    setTimeout(() => {
      if (menuRef.current) {
        uiStore.menuWidth = menuRef.current.getBoundingClientRect().width
      }
    }, 0)

    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  return (
    <>
      <nav className='toolbar' ref={menuRef}>
        <ul className='toolbar__list'>
          <ToolbarMenuItem
            icon={<HugeiconsIcon icon={UserIcon} size={18} strokeWidth={1.5} />}
            menuType={menuItemsMap.USER}
            className='toolbar__btn--icon-only'
          >
            <AccountMenuItems onLogout={logout} />
          </ToolbarMenuItem>
          <ToolbarMenuItem title={t('TOOLBAR.FILE')} menuType={menuItemsMap.FILE}>
            <ImportButtons
              uiStore={uiStore}
              t={t}
              openMarkerFileMenu={openMarkerFileMenu}
              openShapeFileMenu={openShapeFileMenu}
            />
          </ToolbarMenuItem>
          <ToolbarMenuItem title={t('TOOLBAR.TOOLS')} menuType={menuItemsMap.TOOLS}>
            <a href={`${process.env.CORPORATE_URL}/product/population-explorer-api/`} target='_blank' rel='noreferrer'>{t('TOOLBAR.API')}</a>
          </ToolbarMenuItem>
          <ToolbarMenuItem title={t('TOOLBAR.HELP')} menuType={menuItemsMap.HELP}>
            <a href='https://populationexplorer.atlassian.net/servicedesk/customer/portal/1' target='_blank' rel='noreferrer'>{t('TOOLBAR.SUPPORT')}</a>
          </ToolbarMenuItem>
          <ToolbarMenuItem title={t('TOOLBAR.VIEW')} menuType={menuItemsMap.VIEW}>
            <ViewMenuItems />
          </ToolbarMenuItem>
          <ToolbarMenuItem title={t('TOOLBAR.MANAGE')} menuType={menuItemsMap.MANAGE}>
            <button type='button' onClick={() => manageModalStore.open('status')}>{t('TOOLBAR.STATUS')}</button>
            <button type='button' onClick={() => manageModalStore.open('tags')}>{t('TOOLBAR.TAGS')}</button>
            <button type='button' onClick={() => manageModalStore.open('fields')}>{t('TOOLBAR.ADD_FIELDS')}</button>
          </ToolbarMenuItem>
          <ToolbarMenuItem
            menuType={menuItemsMap.LANGUAGE}
            className='toolbar__btn--lang'
            icon={
              <>
                <img
                  className='language-flag'
                  src={uiStore.activeLang.FLAG}
                  alt={`${uiStore.activeLang.NAME} flag`}
                />
                <span className='language-code'>
                  {uiStore.activeLang.COUNTRY_CODE ? uiStore.activeLang.COUNTRY_CODE.toUpperCase() : 'EN'}
                </span>
                <HugeiconsIcon icon={ChevronDownIcon} size={14} strokeWidth={1.5} className='toolbar__chevron' />
              </>
            }
          >
            <Language menuRef={menuRef} />
          </ToolbarMenuItem>
        </ul>
        <button
          type='button'
          className='toolbar__btn toolbar__btn--icon-only toolbar__toggle-btn'
          onClick={() => uiStore.toggleSideBarMenu()}
          aria-label={uiStore.sideMenuHidden ? 'Expand sidebar' : 'Collapse sidebar'}
          title={uiStore.sideMenuHidden ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <SidebarToggleIcon />
        </button>
      </nav>
      <ManageModal />
    </>
  )
}

export default inject('uiStore', 'dataStore', 'selectionStore', 'manageModalStore')(withTranslation()(Toolbar))
